/**
 * Sample location data for Insights → Location.
 * Mirrors the phone-app payload (AddBuyerLocationRequestDTO) so we can
 * review KPIs before wiring real pings.
 *
 * activity: moving | stationary | break | unknown
 * speed: meters/second
 * accuracy: meters
 * heading: degrees 0–360
 * battery: 0–100
 * recordedAt: epoch ms
 */

export const SAMPLE_BUYERS = [
  { userId: "sample-kar", firstName: "Kar", lastName: "Patel", email: "kar@abcattech.com" },
  { userId: "sample-james", firstName: "James", lastName: "Ortiz", email: "james@abcattech.com" },
  { userId: "sample-priya", firstName: "Priya", lastName: "Shah", email: "priya@abcattech.com" },
  { userId: "sample-deshawn", firstName: "DeShawn", lastName: "Cole", email: "deshawn@abcattech.com" },
  { userId: "sample-elena", firstName: "Elena", lastName: "Ruiz", email: "elena@abcattech.com" },
  { userId: "sample-tom", firstName: "Tom", lastName: "Nguyen", email: "tom@abcattech.com" },
];

const now = () => Date.now();
const ago = (mins, extraMs = 0) => now() - mins * 60 * 1000 - extraMs;

export const SAMPLE_LIVE = [
  {
    userId: "sample-kar",
    userName: "Kar Patel",
    lat: 32.8124,
    lng: -96.8371,
    accuracy: 8,
    speed: 12.4,
    heading: 312,
    altitude: 138,
    battery: 72,
    activity: "moving",
    isBreak: false,
    recordedAt: ago(0.4),
    deviceTypeId: "ios",
    appVersion: "3.4.1",
  },
  {
    userId: "sample-james",
    userName: "James Ortiz",
    lat: 33.755,
    lng: -84.39,
    accuracy: 12,
    speed: 0.2,
    heading: 0,
    altitude: 305,
    battery: 54,
    activity: "stationary",
    isBreak: false,
    recordedAt: ago(2),
    deviceTypeId: "android",
    appVersion: "3.4.0",
  },
  {
    userId: "sample-priya",
    userName: "Priya Shah",
    lat: 29.7604,
    lng: -95.3698,
    accuracy: 15,
    speed: 0,
    heading: 180,
    altitude: 14,
    battery: 41,
    activity: "break",
    isBreak: true,
    recordedAt: ago(6),
    deviceTypeId: "android",
    appVersion: "3.4.1",
  },
  {
    userId: "sample-deshawn",
    userName: "DeShawn Cole",
    lat: 41.8781,
    lng: -87.6298,
    accuracy: 48,
    speed: 8.1,
    heading: 88,
    altitude: 182,
    battery: 88,
    activity: "moving",
    isBreak: false,
    recordedAt: ago(1),
    deviceTypeId: "ios",
    appVersion: "3.3.9",
  },
  {
    userId: "sample-elena",
    userName: "Elena Ruiz",
    lat: 33.4484,
    lng: -112.074,
    accuracy: 9,
    speed: 15.6,
    heading: 24,
    altitude: 331,
    battery: 11,
    activity: "moving",
    isBreak: false,
    recordedAt: ago(0.8),
    deviceTypeId: "android",
    appVersion: "3.4.1",
  },
  {
    userId: "sample-tom",
    userName: "Tom Nguyen",
    lat: 35.2271,
    lng: -80.8431,
    accuracy: 22,
    speed: 0,
    heading: 0,
    altitude: 229,
    battery: 19,
    activity: "stationary",
    isBreak: false,
    recordedAt: ago(38),
    deviceTypeId: "ios",
    appVersion: "3.4.0",
  },
];

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function at(dateStr, h, m) {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setHours(h, m, 0, 0);
  return d.getTime();
}

function pointsAlong(from, to, startMs, endMs, stepMs, extras) {
  const n = Math.max(2, Math.round((endMs - startMs) / stepMs));
  const out = [];
  for (let i = 0; i <= n; i += 1) {
    const t = i / n;
    const jitter = extras.activity === "moving" ? (i % 3) * 0.00008 : 0;
    out.push({
      lat: lerp(from[0], to[0], t) + jitter,
      lng: lerp(from[1], to[1], t) - jitter * 0.6,
      recordedAt: Math.round(startMs + (endMs - startMs) * t),
      accuracy: extras.accuracy,
      speed: extras.speed,
      heading: extras.heading,
      altitude: extras.altitude,
      battery: extras.batteryStart != null
        ? Math.round(lerp(extras.batteryStart, extras.batteryEnd, t))
        : extras.battery,
      activity: extras.activity,
      isBreak: extras.isBreak || false,
      deviceTypeId: "ios",
      appVersion: "3.4.1",
    });
  }
  return out;
}

const DALLAS = {
  start: [32.7767, -96.797],
  metro: [32.82, -96.84],
  green: [32.91, -96.73],
  lunch: [32.953, -96.821],
  summit: [32.88, -96.63],
  short: [32.83, -96.71],
};

export function getSampleDayTrail(dateStr, userId) {
  const buyer =
    SAMPLE_BUYERS.find((b) => b.userId === userId) || SAMPLE_BUYERS[0];
  const date = dateStr || new Date().toISOString().slice(0, 10);

  const trail = [
    ...pointsAlong(DALLAS.start, DALLAS.metro, at(date, 7, 15), at(date, 7, 55), 60000, {
      activity: "moving",
      isBreak: false,
      accuracy: 9,
      speed: 14,
      heading: 310,
      altitude: 140,
      batteryStart: 96,
      batteryEnd: 93,
    }),
    ...pointsAlong(DALLAS.metro, DALLAS.metro, at(date, 8, 0), at(date, 9, 20), 180000, {
      activity: "stationary",
      isBreak: false,
      accuracy: 11,
      speed: 0.1,
      heading: 0,
      altitude: 142,
      batteryStart: 93,
      batteryEnd: 88,
    }),
    ...pointsAlong(DALLAS.metro, DALLAS.green, at(date, 9, 22), at(date, 9, 58), 60000, {
      activity: "moving",
      isBreak: false,
      accuracy: 8,
      speed: 16,
      heading: 20,
      altitude: 150,
      batteryStart: 88,
      batteryEnd: 85,
    }),
    ...pointsAlong(DALLAS.green, DALLAS.green, at(date, 10, 0), at(date, 11, 10), 180000, {
      activity: "stationary",
      isBreak: false,
      accuracy: 10,
      speed: 0,
      heading: 90,
      altitude: 155,
      batteryStart: 85,
      batteryEnd: 80,
    }),
    ...pointsAlong(DALLAS.green, DALLAS.lunch, at(date, 11, 12), at(date, 12, 5), 60000, {
      activity: "moving",
      isBreak: false,
      accuracy: 12,
      speed: 13,
      heading: 300,
      altitude: 160,
      batteryStart: 80,
      batteryEnd: 76,
    }),
    ...pointsAlong(DALLAS.lunch, DALLAS.lunch, at(date, 12, 10), at(date, 12, 45), 180000, {
      activity: "break",
      isBreak: true,
      accuracy: 14,
      speed: 0,
      heading: 0,
      altitude: 162,
      batteryStart: 76,
      batteryEnd: 74,
    }),
    ...pointsAlong(DALLAS.lunch, DALLAS.summit, at(date, 12, 48), at(date, 13, 12), 60000, {
      activity: "moving",
      isBreak: false,
      accuracy: 9,
      speed: 15,
      heading: 95,
      altitude: 148,
      batteryStart: 74,
      batteryEnd: 71,
    }),
    ...pointsAlong(DALLAS.summit, DALLAS.summit, at(date, 13, 15), at(date, 14, 40), 180000, {
      activity: "stationary",
      isBreak: false,
      accuracy: 10,
      speed: 0.2,
      heading: 180,
      altitude: 145,
      batteryStart: 71,
      batteryEnd: 64,
    }),
    ...pointsAlong(DALLAS.summit, DALLAS.short, at(date, 14, 42), at(date, 15, 0), 60000, {
      activity: "moving",
      isBreak: false,
      accuracy: 8,
      speed: 12,
      heading: 250,
      altitude: 142,
      batteryStart: 64,
      batteryEnd: 62,
    }),
    ...pointsAlong(DALLAS.short, DALLAS.short, at(date, 15, 2), at(date, 15, 20), 120000, {
      activity: "stationary",
      isBreak: false,
      accuracy: 16,
      speed: 0,
      heading: 0,
      altitude: 140,
      batteryStart: 62,
      batteryEnd: 61,
    }),
    ...pointsAlong(DALLAS.short, DALLAS.start, at(date, 15, 22), at(date, 16, 35), 60000, {
      activity: "moving",
      isBreak: false,
      accuracy: 10,
      speed: 17,
      heading: 200,
      altitude: 138,
      batteryStart: 61,
      batteryEnd: 48,
    }),
  ];

  const events = [
    {
      type: "stop",
      label: "Stop — Metro Recycling (NW Dallas)",
      startAt: at(date, 8, 0),
      endAt: at(date, 9, 20),
      lat: DALLAS.metro[0],
      lng: DALLAS.metro[1],
      durationMins: 80,
    },
    {
      type: "stop",
      label: "Stop — Green Catalytics",
      startAt: at(date, 10, 0),
      endAt: at(date, 11, 10),
      lat: DALLAS.green[0],
      lng: DALLAS.green[1],
      durationMins: 70,
    },
    {
      type: "break",
      label: "Break — lunch",
      startAt: at(date, 12, 10),
      endAt: at(date, 12, 45),
      lat: DALLAS.lunch[0],
      lng: DALLAS.lunch[1],
      durationMins: 35,
    },
    {
      type: "stop",
      label: "Stop — Summit Scrap",
      startAt: at(date, 13, 15),
      endAt: at(date, 14, 40),
      lat: DALLAS.summit[0],
      lng: DALLAS.summit[1],
      durationMins: 85,
    },
    {
      type: "stop",
      label: "Stop — short customer visit",
      startAt: at(date, 15, 2),
      endAt: at(date, 15, 20),
      lat: DALLAS.short[0],
      lng: DALLAS.short[1],
      durationMins: 18,
    },
  ];

  const first = trail[0];
  const last = trail[trail.length - 1];
  const speeds = trail.map((p) => Number(p.speed) || 0);
  const accuracies = trail.map((p) => Number(p.accuracy) || 0);
  const moving = trail.filter((p) => p.activity === "moving");

  return {
    userId: buyer.userId,
    userName: `${buyer.firstName} ${buyer.lastName}`,
    date,
    trail,
    events,
    summary: {
      pointCount: trail.length,
      distanceKm: 86.4,
      activeMins: 248,
      stopMins: 253,
      breakMins: 35,
      stopCount: 4,
      breakCount: 1,
      firstPingAt: first.recordedAt,
      lastPingAt: last.recordedAt,
      avgSpeedKmh: 42,
      maxSpeedKmh: 61,
      avgAccuracyM: Math.round(
        accuracies.reduce((s, n) => s + n, 0) / Math.max(1, accuracies.length)
      ),
      batteryStart: first.battery,
      batteryEnd: last.battery,
      movingPingCount: moving.length,
      maxSpeedMps: Math.max(...speeds),
    },
  };
}

function hashString(value) {
  const str = String(value || "");
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseDate(dateStr) {
  return new Date(`${dateStr}T12:00:00`);
}

export function shiftReportDate(dateStr, period, dir) {
  const d = parseDate(dateStr);
  if (period === "week") d.setDate(d.getDate() + 7 * dir);
  else if (period === "month") d.setMonth(d.getMonth() + dir);
  else d.setDate(d.getDate() + dir);
  return isoDate(d);
}

export function getReportRange(period, dateStr) {
  const selected = parseDate(dateStr);
  if (period === "week") {
    const start = new Date(selected);
    const day = start.getDay();
    const toMon = day === 0 ? -6 : 1 - day;
    start.setDate(start.getDate() + toMon);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start: isoDate(start), end: isoDate(end) };
  }
  if (period === "month") {
    const start = new Date(selected.getFullYear(), selected.getMonth(), 1);
    const end = new Date(selected.getFullYear(), selected.getMonth() + 1, 0);
    return { start: isoDate(start), end: isoDate(end) };
  }
  const day = isoDate(selected);
  return { start: day, end: day };
}

export function formatReportRangeLabel(period, dateStr) {
  const { start, end } = getReportRange(period, dateStr);
  const startD = parseDate(start);
  const endD = parseDate(end);
  const dayFmt = { weekday: "short", month: "short", day: "numeric", year: "numeric" };
  if (period === "day") return startD.toLocaleDateString("en-US", dayFmt);
  if (period === "month") {
    return startD.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }
  const sameYear = startD.getFullYear() === endD.getFullYear();
  return `${startD.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  })} – ${endD.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
}

function eachDate(startStr, endStr) {
  const dates = [];
  const cur = parseDate(startStr);
  const end = parseDate(endStr);
  while (cur <= end) {
    dates.push(isoDate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

function emptyDayMetrics() {
  return {
    daysWorked: 0,
    distanceKm: 0,
    drivingMins: 0,
    stopCount: 0,
    stopMins: 0,
    breakCount: 0,
    breakMins: 0,
    pingCount: 0,
    avgSpeedKmh: 0,
  };
}

function sampleDayMetrics(userId, dateStr) {
  const d = parseDate(dateStr);
  const weekday = d.getDay();
  const seed = hashString(`${userId}:${dateStr}`);
  if (weekday === 0) return emptyDayMetrics();
  if (weekday === 6 && seed % 2 === 0) return emptyDayMetrics();
  if (seed % 11 === 0) return emptyDayMetrics();

  const distanceKm = 38 + (seed % 72);
  const drivingMins = 160 + (seed % 140);
  const stopCount = 3 + (seed % 5);
  const stopMins = 140 + (seed % 160);
  const breakCount = 1 + (seed % 2);
  const breakMins = 20 + (seed % 35);
  const pingCount = 90 + (seed % 90);
  const avgSpeedKmh = 32 + (seed % 22);

  return {
    daysWorked: 1,
    distanceKm,
    drivingMins,
    stopCount,
    stopMins,
    breakCount,
    breakMins,
    pingCount,
    avgSpeedKmh,
  };
}

function addMetrics(a, b) {
  return {
    daysWorked: a.daysWorked + b.daysWorked,
    distanceKm: a.distanceKm + b.distanceKm,
    drivingMins: a.drivingMins + b.drivingMins,
    stopCount: a.stopCount + b.stopCount,
    stopMins: a.stopMins + b.stopMins,
    breakCount: a.breakCount + b.breakCount,
    breakMins: a.breakMins + b.breakMins,
    pingCount: a.pingCount + b.pingCount,
    avgSpeedKmh: 0,
  };
}

function roundKm(n) {
  return Math.round(n * 10) / 10;
}

/**
 * Sample day / week / month rollup used by Insights → Location → Reports.
 * Later this is replaced by aggregating BuyerLocation pings server-side.
 */
export function getSamplePeriodReport(period, dateStr, buyers = SAMPLE_BUYERS) {
  const range = getReportRange(period, dateStr);
  const dates = eachDate(range.start, range.end);
  const people = buyers.length ? buyers : SAMPLE_BUYERS;

  const buyerRows = people.map((buyer) => {
    let totals = emptyDayMetrics();
    const daily = dates.map((day) => {
      const metrics = sampleDayMetrics(buyer.userId, day);
      totals = addMetrics(totals, metrics);
      return { date: day, ...metrics };
    });
    const avgKm =
      totals.daysWorked > 0 ? roundKm(totals.distanceKm / totals.daysWorked) : 0;
    const speedDays = daily.filter((d) => d.daysWorked);
    const avgSpeedKmh = speedDays.length
      ? Math.round(speedDays.reduce((s, d) => s + d.avgSpeedKmh, 0) / speedDays.length)
      : 0;
    return {
      userId: buyer.userId,
      userName: `${buyer.firstName || ""} ${buyer.lastName || ""}`.trim() || buyer.email,
      ...totals,
      distanceKm: roundKm(totals.distanceKm),
      avgKmPerDay: avgKm,
      avgSpeedKmh,
      daily,
    };
  });

  buyerRows.sort((a, b) => b.distanceKm - a.distanceKm);

  const team = buyerRows.reduce((acc, row) => addMetrics(acc, row), emptyDayMetrics());
  team.distanceKm = roundKm(team.distanceKm);
  team.buyersActive = buyerRows.filter((r) => r.daysWorked > 0).length;
  team.avgKmPerBuyer =
    team.buyersActive > 0 ? roundKm(team.distanceKm / team.buyersActive) : 0;

  const buckets = dates.map((day) => {
    const ofDay = buyerRows.reduce((acc, row) => {
      const m = row.daily.find((d) => d.date === day) || emptyDayMetrics();
      return addMetrics(acc, m);
    }, emptyDayMetrics());
    return {
      date: day,
      label: parseDate(day).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
      distanceKm: roundKm(ofDay.distanceKm),
      drivingMins: ofDay.drivingMins,
      stopCount: ofDay.stopCount,
      breakCount: ofDay.breakCount,
      daysWorked: ofDay.daysWorked,
    };
  });

  return {
    period,
    ...range,
    label: formatReportRangeLabel(period, dateStr),
    buyers: buyerRows,
    team,
    buckets,
  };
}

export const APP_PAYLOAD_FIELDS = [
  { key: "lat", required: true, note: "Required. GPS latitude." },
  { key: "lng", required: true, note: "Required. GPS longitude." },
  { key: "recordedAt", required: false, note: "Device epoch ms. Server time is used if missing." },
  {
    key: "activity",
    required: false,
    note: "moving | stationary | break | unknown. Drives live color and stop/break KPIs.",
  },
  { key: "isBreak", required: false, note: "true when buyer starts an explicit break in the app." },
  { key: "accuracy", required: false, note: "GPS accuracy in meters. Flags poor location quality." },
  { key: "speed", required: false, note: "m/s. Used for moving vs stop and avg/max speed." },
  { key: "heading", required: false, note: "Degrees 0–360. Direction of travel on the map." },
  { key: "altitude", required: false, note: "Meters. Optional, rarely needed for KPIs." },
  { key: "battery", required: false, note: "0–100. Low-battery and drain-over-day KPIs." },
  { key: "deviceTypeId", required: false, note: "ios | android. Helps debug app issues." },
  { key: "appVersion", required: false, note: "App build, e.g. 3.4.1." },
  { key: "userId / userName", required: false, note: "Filled from the logged-in session on the server." },
];
